/**
 * route_reveal — small pure helpers that decide where the "Reveal"
 * button on an accepted recommendation should jump.
 *
 * Pulled out of ReflectScreen so:
 *   - the rule is testable without React or a live browser
 *   - the same heuristic is reused by Today focus card + chat Action
 *     Engine in CLCD phase 4 (when we proper-extract a reveal service)
 *
 * Today: only `pickRevealKind` ships. Future phases will add an
 * entity-shaped `computeRevealTarget({ kind, id, when })` here.
 */

export type RevealKind = "today" | "plan" | "goal";

/** Decide where to send the user after they accept a schedule_block
 *  recommendation:
 *
 *    same calendar day as `now` → "today" (block sits in today's timeline)
 *    strictly future            → "plan"  (Today doesn't render future days)
 *    null start                 → "goal"  (legacy fallback when we
 *                                          couldn't resolve a date)
 *    past start                 → "goal"  (shouldn't happen given the
 *                                          past-time guard in
 *                                          resolveScheduleStart, but
 *                                          choosing a safe fallback) */
export function pickRevealKind(
  scheduledStart: Date | null,
  now: Date = new Date(),
): RevealKind {
  if (!scheduledStart) return "goal";
  const sameDay =
    scheduledStart.getFullYear() === now.getFullYear() &&
    scheduledStart.getMonth() === now.getMonth() &&
    scheduledStart.getDate() === now.getDate();
  if (sameDay) return "today";
  if (scheduledStart.getTime() > now.getTime()) return "plan";
  return "goal";
}
